import { CDN_BASE_URL } from './consts/url';
import type { WindowWithOrgData } from './types/private-label-types';

type Args = {
  orgBrandOverride?: string;
  filenameOverride?: string;
  isProd?: boolean;
};

/**
 * Generates the URL for the private label logo in the CDN.
 *
 * This function constructs the URL for the private label logo based on the optional organization brand
 * and an optional filename override. If the organization brand is not provided, it falls back to the
 * global `window.__ORG_INFO__` object to look for the orgBrand (requires app-server private label middleware).
 * If the filename override is not provided, it defaults to 'logo.svg'.
 *
 * @param {Object} [params] - The parameters for generating the logo URL.
 * @param {string} [params.orgBrandOverride] - The organization brand to use in the URL. Defaults to `window.__ORG_INFO__?.orgBrand`. This "orgBrand" value associates with the foldername in the CDN. This must be provided if this function is used in an app that does not use the app-server.
 * @param {string} [params.filenameOverride] - The filename to use for the logo. Defaults to 'logo.svg'.
 * @param {boolean} [params.isProd] - Whether the environment is production. Defaults to `true` to mimic pre-existing behavior.
 * @returns {string} The constructed URL for the private label logo.
 */
export const getPrivateLabelLogoHref = ({ orgBrandOverride, filenameOverride, isProd = true }: Args = {}) => {
  const env = !isProd ? '/test' : '';
  return `${CDN_BASE_URL}${env}/apps/frontend-private-label/${orgBrandOverride ?? (window as WindowWithOrgData).__ORG_INFO__?.orgBrand}/${filenameOverride ?? 'logo.svg'}`;
};
